/*
  Click attribution.

  Google only learns from what we tell it. Until now the only thing it could see
  was a web checkout, so it believed a Tile Touch customer was worth about $77.
  The real money is the enquiry Charles closes weeks later as a counter sale, and
  Google has never been told those exist.

  This file catches the click that brought someone here and keeps hold of it, so
  that when a sale finally closes we can hand Google back the real number.

  What it does:
    1. Reads the click id off the landing URL (gclid, or gbraid / wbraid on iOS,
       plus msclkid so Bing is not thrown away) and stores it for 90 days, which
       is Google's upload window.
    2. Writes it onto the cart as a private attribute, so it rides through to the
       Shopify order and is readable from the admin API.
    3. Exposes window.ttClick() so anything that learns an email address, the
       sample flow included, can tie that person to their click.

  Last touch wins. A click id from today replaces one from last month, because
  that is the click Google attributes the sale to.
*/
(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.fetch) return;

  var STORE_KEY = 'tt_click';
  var PUSHED_KEY = 'tt_click_pushed';
  var MAX_AGE_DAYS = 90;

  // Ordered by how much we trust them. gclid is the real one; gbraid and wbraid
  // are what Google sends instead when iOS strips the gclid.
  var CLICK_PARAMS = ['gclid', 'gbraid', 'wbraid', 'msclkid'];
  var UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

  function safeParse(raw) {
    if (!raw) return null;
    try {
      var v = JSON.parse(raw);
      return v && typeof v === 'object' ? v : null;
    } catch (e) {
      return null;
    }
  }

  function readCookie(name) {
    var parts = ('; ' + document.cookie).split('; ' + name + '=');
    if (parts.length !== 2) return null;
    try {
      return decodeURIComponent(parts.pop().split(';').shift());
    } catch (e) {
      return null;
    }
  }

  function writeCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 864e5).toUTCString();
    var secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      name + '=' + encodeURIComponent(value) +
      '; expires=' + expires + '; path=/; SameSite=Lax' + secure;
  }

  // localStorage throws in some privacy modes, so every use is guarded.
  function lsGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function lsSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* ignore */ }
  }

  function stored() {
    return safeParse(readCookie(STORE_KEY)) || safeParse(lsGet(STORE_KEY));
  }

  function persist(record) {
    var raw = JSON.stringify(record);
    writeCookie(STORE_KEY, raw, MAX_AGE_DAYS);
    lsSet(STORE_KEY, raw);
  }

  function fromUrl() {
    var params;
    try {
      params = new URLSearchParams(location.search);
    } catch (e) {
      return null;
    }

    var id = null;
    var type = null;
    for (var i = 0; i < CLICK_PARAMS.length; i++) {
      var v = params.get(CLICK_PARAMS[i]);
      if (v) { id = v; type = CLICK_PARAMS[i]; break; }
    }
    if (!id) return null;

    var utm = {};
    for (var j = 0; j < UTM_PARAMS.length; j++) {
      var u = params.get(UTM_PARAMS[j]);
      if (u) utm[UTM_PARAMS[j].replace('utm_', '')] = u;
    }

    return {
      id: id,
      type: type,
      at: new Date().toISOString(),
      landing: location.pathname,
      utm: utm
    };
  }

  function expired(record) {
    if (!record || !record.at) return true;
    var age = Date.now() - new Date(record.at).getTime();
    return !(age >= 0) || age > MAX_AGE_DAYS * 864e5;
  }

  // Capture, last touch wins.
  var fresh = fromUrl();
  var current = stored();
  if (fresh) {
    // Keep the very first click we ever saw purely for reporting. It never
    // goes to Google; Google wants the click that closed the sale.
    fresh.first = (current && current.first) || (current && current.at) || fresh.at;
    persist(fresh);
    current = fresh;
  } else if (expired(current)) {
    current = null;
  }

  /* Public handle for anything that later learns who this person is. */
  window.ttClick = function () {
    return current ? {
      id: current.id,
      type: current.type,
      at: current.at,
      utm: current.utm || {}
    } : null;
  };

  if (!current) return;

  /* Tell the worker who this person is, the moment we find out.

     Checkout orders already carry the click id as a cart attribute, but most
     Tile Touch revenue never reaches checkout: someone orders a sample or asks
     to be told when a tile is back, then Charles closes them weeks later. Those
     journeys only ever hand us an email address, so we watch for one anywhere
     on the page rather than wiring up each form by hand.

     This only ever fires for people who arrived on a paid click. An organic
     visitor sends nothing, because we returned above. */
  var ATTR_ENDPOINT = 'https://tiletouch-email.snowy-hill-e0b7.workers.dev/attr';
  var EMAIL_RE = /^[^\s@<>"']{1,64}@[^\s@<>"']{1,190}\.[a-z]{2,}$/i;
  var reported = {};

  function report(email) {
    var addr = String(email || '').trim().toLowerCase();
    if (!EMAIL_RE.test(addr) || reported[addr]) return;
    reported[addr] = true;

    var payload = JSON.stringify({
      email: addr,
      click_id: current.id,
      click_type: current.type,
      click_at: current.at,
      source: 'web'
    });

    // sendBeacon survives the page unloading on submit; fetch is the fallback.
    try {
      if (navigator.sendBeacon) {
        var ok = navigator.sendBeacon(ATTR_ENDPOINT, new Blob([payload], { type: 'application/json' }));
        if (ok) return;
      }
    } catch (e) { /* fall through */ }

    fetch(ATTR_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
      mode: 'cors'
    }).catch(function () { /* nothing the shopper needs to know about */ });
  }

  function harvest(root) {
    if (!root || !root.querySelectorAll) return;
    var fields = root.querySelectorAll('input[type="email"], input[name*="email" i]');
    for (var i = 0; i < fields.length; i++) {
      if (fields[i].value) report(fields[i].value);
    }
  }

  // A filled-in email field, however it got filled in.
  document.addEventListener('change', function (e) {
    var el = e.target;
    if (!el || el.tagName !== 'INPUT') return;
    var type = (el.getAttribute('type') || '').toLowerCase();
    var name = (el.getAttribute('name') || '').toLowerCase();
    if (type === 'email' || name.indexOf('email') !== -1) report(el.value);
  }, true);

  // Anything submitted, including forms built by apps after page load.
  document.addEventListener('submit', function (e) {
    harvest(e.target);
  }, true);

  /* Ride the click id through to the Shopify order.

     Attribute names start with an underscore so Shopify treats them as private:
     they stay off the order status page and out of customer emails, but remain
     readable in the admin API, which is where the export reads them from.

     Only paid clicks get this far, and only once per click id, so this adds no
     request at all for organic traffic. */
  if (lsGet(PUSHED_KEY) === current.id) return;

  var attributes = {
    _click_id: current.id,
    _click_type: current.type,
    _click_at: current.at
  };
  if (current.utm && current.utm.campaign) attributes._click_campaign = current.utm.campaign;

  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: attributes })
  })
    .then(function (res) {
      if (res.ok) lsSet(PUSHED_KEY, current.id);
    })
    .catch(function () {
      // A failed write is not worth bothering the shopper about. The cookie
      // still holds the click id, so the next page view tries again.
    });
})();
